import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Paper,
  Typography,
  TableContainer,
  Icon,
  Tooltip, Switch,
  Box,
  Alert,
  TablePagination,
  InputAdornment
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { ItemsContext } from "../App";
import { getApiToken } from "../authUtils";
import { useMsal } from '@azure/msal-react';
import DownloadBillpdf from "../components/DownloadBillpdf";
import { gstRate, withGst } from "../shared/gst";

const urlconfig = require('../config/urlconfig');

const TransportationDashboard = () => {
  const { items, refreshItems } = useContext(ItemsContext);

  const { instance, accounts } = useMsal();
  const account = accounts && accounts.length > 0 ? accounts[0] : null;

  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null);
  const [fileUrls, setFileUrls] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [viewtotalCost, setViewtotalCost] = useState(null)
  const [transportationForms, setTransportationForms] = useState([
    { modeOfTransportation: "", actualTransportationCost: "", referenceID:"", tripDate:"", spocName:"", transaction:""},
  ]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const pdfContentRef = useRef(); // for capturing dialog content
  const pdfRef = useRef();     // for calling download function

  useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedSearchQuery(searchQuery);
      }, 500); // 500ms debounce
  
      return () => clearTimeout(handler);
    }, [searchQuery]);
    useEffect(() => {
      let mounted = true;
      const fetchData = async () => {
        try {
          if (!mounted) return;
          setLoading(true);
          // refreshItems should accept (search, page, size)
          await refreshItems(debouncedSearchQuery, page, rowsPerPage);
        } catch (err) {
          console.error("Error refreshing items:", err);
        } finally {
          if (mounted) setLoading(false);
        }
      };
      fetchData();
      return () => {
        mounted = false;
      };
    }, [debouncedSearchQuery, page, rowsPerPage, refreshItems]);


  const handleTransportationAdd = (item) => {
  setSelectedItem(item);
  setOpenDialog(true);

  // Preload existing entries if present, else start with one empty
  if (item.transportationDetails && item.transportationDetails.length > 0) {
    setTransportationForms(
      item.transportationDetails.map(d => ({
        modeOfTransportation: d.modeOfTransportation || "",
        actualTransportationCost: d.actualTransportationCost || "",
        referenceID: d.referenceID || "",
        tripDate: d.tripDate ? d.tripDate.split("T")[0] : "",
        spocName: d.spocName || "",
        transaction : d.transaction || "",
      }))
    );
  } else {
    setTransportationForms([
      { modeOfTransportation: "", actualTransportationCost: "", referenceID:"", tripDate:"", spocName:"", transaction: false }
    ]);
  }
};

const handleviewTransportation = (item)=>{
  setSelectedItem(item)
  setOpenViewDialog(true);
}


  const handleClose = () => {
    setOpenDialog(false);
    setSelectedItem(null);
    setOpenViewDialog(false);
  };

  const handleAddAnotherTransportation = () => {
    setTransportationForms([
      ...transportationForms,
      { modeOfTransportation: "", actualTransportationCost: "" },
    ]);
  };

  const handleFormChange = (index, field, value) => {
    const updatedForms = [...transportationForms];
    updatedForms[index][field] = value;
    if(field==="file"){
      updatedForms[index]["fileUrl"] = value;
    }
    setTransportationForms(updatedForms);
  };

  const handleRemoveForm = (index) => {
    if (transportationForms[index].transaction===true){
      alert("Cannot remove the form, as transaction is paid");
      return
    }
    const updatedForms = transportationForms.filter((_, i) => i !== index);
    setTransportationForms(updatedForms.length > 0 ? updatedForms : [{ modeOfTransportation: "", actualTransportationCost: "",transaction:"unpaid" }]);
  };

  const sendAuthenticatedRequest = async (url, method, body) => {
    try {
      const accessToken = await getApiToken(instance, account);
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: response.statusText,
        }));
        throw new Error(`API Error: ${response.status} - ${errorData.message || "Unknown error"}`);
      }

      const contentType = response.headers.get("content-type");
      return contentType?.includes("application/json") ? response.json() : response.text();
    } catch (error) {
      console.error("Authenticated API call failed:", error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
      e.preventDefault();
      if (!selectedItem) return;

      try {
        const accessToken = await getApiToken(instance, account);

        const currentDetails = selectedItem.transportationDetails || [];

        const updatedTransportationDetails = await Promise.all(
          transportationForms.map(async (formData, index) => {
            let fileUrl = formData.fileUrl || currentDetails[index]?.fileUrl || null;
            if (formData.file) {
              const uploadData = new FormData();
              uploadData.append("file", formData.file);
              const res = await fetch(
                `${urlconfig.backend_api_base_url}/api/v2/transportation/${selectedItem.id}/upload`,
                {
                  method: "POST",
                  headers: { Authorization: `Bearer ${accessToken}` },
                  body: uploadData,
                }
              );
              if (!res.ok) throw new Error("File upload failed");
              fileUrl = await res.text(); // backend returns S3 URL
            }

            return {
              modeOfTransportation: formData.modeOfTransportation.trim(),
              actualTransportationCost: Number(formData.actualTransportationCost),
              referenceID: formData.referenceID?.trim(),
              tripDate: formData.tripDate,
              spocName: formData.spocName?.trim(),
              transaction: !!formData.transaction,
              fileUrl,
            };
          })
        );


        await sendAuthenticatedRequest(
          `${urlconfig.backend_api_base_url}/api/v2/edit-costsheet/${selectedItem.id || selectedItem._id}`,
          "PUT",
          { transportationDetails: updatedTransportationDetails }
        );

        refreshItems();
        setSelectedItem(null);
        setOpenDialog(false);
        setTransportationForms([{ modeOfTransportation: "", actualTransportationCost: "", referenceID: "", tripDate: "", spocName: "", transaction: false, fileUrl: null }]);
      } catch (err) {
        console.error("Update failed:", err);
        alert("Failed to save transportation details.");
      }
    };

    useEffect(() => {
      if (openViewDialog && selectedItem?.transportationDetails) {
        const fetchUrls = async () => {
          const newUrls = {};
          const accessToken = await getApiToken(instance, account);

          for (const d of selectedItem.transportationDetails) {
            if (d.fileUrl) {
              try {
                const res = await fetch(
                  `${urlconfig.backend_api_base_url}/api/v2/transportation/${selectedItem.id}/bill?key=${encodeURIComponent(d.fileUrl)}`,
                  {
                    method: "GET",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${accessToken}`,
                    },
                  }
                );

                if (!res.ok) throw new Error("Failed to fetch file URL");

                const data = await res.json();
                newUrls[d.fileUrl] = data.url;
              } catch (error) {
                console.error("Error fetching file URL for", d.fileUrl, error);
              }
            }
          }

          setFileUrls(newUrls);
        };

        fetchUrls();
      }
    }, [openViewDialog, selectedItem]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
      };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
    };


  return (
    <>
      <h2>Transportation Dashboard</h2>
      <p>Manage transportation details for sales orders.</p>

      <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Sales Order No, Client Name, SPOC, Details..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setSearchQuery('');
                        setDebouncedSearchQuery('');
                        setPage(0);
                      }}
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

      <Paper sx={{ width: '100%', mt:2 }}>
        <TableContainer>
      <Table stickyHeader aria-label="sticky table">
        <TableHead>
          <TableRow>
            <TableCell>Creation Date</TableCell>
            <TableCell><div>SO No. </div>
            Created By
            </TableCell>

            <TableCell>Budgeted Transportation Cost<br />incl. GST (₹)</TableCell>
            <TableCell>Actual Transportation Cost<br />incl. GST (₹)</TableCell>
            <TableCell>Variance (₹)</TableCell>
            <TableCell>Variance (%)</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.content && items.content.length > 0 ? (
            items.content.map((item, index) => {
              const approvers =
                Array.isArray(item.approvedBy) &&
                item.approvedBy.filter((name) => name && name.trim() !== "");

              const gstPercent = gstRate(item, 'transportationGST');
              // Vendor bills already include GST, so the budget gets GST added and
              // the variance compares like with like
              const budgetedCost = withGst(item.transportationTotalPp, gstPercent);
              const actualCost = item.transportationDetails?.reduce(
                (sum, d) => sum + (Number(d.actualTransportationCost) || 0),
                0
              ) || 0;

                const variance = budgetedCost - actualCost;

                const variancePercent =
                  budgetedCost === 0
                    ? actualCost === 0
                      ? "0%"
                      : "-100%"
                    : ((variance / budgetedCost) * 100).toFixed(2) + "%";


              return (
                <TableRow key={index}>
                  <TableCell>{item.createdAT ? new Date(item.createdAT).toLocaleDateString() : "N/A"}</TableCell>
                  <TableCell>{item.salesOrderNo || "N/A"}
                    <div>
                      <Typography fontSize="normal" fontWeight="bold" variant="text" >{item.createdBy || "N/A"}</Typography>
                    </div>
                  </TableCell>


                  <TableCell>
                    ₹{(budgetedCost).toFixed(2)}
                    <Typography variant="caption" display="block" color="text.secondary">GST {gstPercent}%</Typography>
                  </TableCell>
                  <TableCell>
                    {item.transportationDetails?.length > 0 ? (
                      <Typography fontWeight="bold" color="success.main">
                        ₹{actualCost.toFixed(2)}
                      </Typography>
                    ) : "0.00"}
                  </TableCell>
                  <TableCell sx={{ color: variance > 0 ? 'green' : variance < 0 ? 'red' : 'inherit', fontWeight: variance !== 0 ? 'bold' : 'normal' }} >₹{(variance).toFixed(2)}</TableCell>
                  <TableCell>{variancePercent}</TableCell>
                  <TableCell>
                      <Box display="flex" gap={1}>
                        <Button
                          variant="contained"
                          onClick={() => handleTransportationAdd(item)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => handleviewTransportation(item)}
                        >
                          View
                        </Button>
                      </Box>
                    </TableCell>

                </TableRow>
              );
            })
          ) : (
            //table row for no data
            <TableRow>
              <TableCell colSpan={10} align="center">
                <Alert severity="info">
                    {searchQuery
                      ? "No matching sales orders found for your search."
                      : "No sales orders available."}
                </Alert>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      </TableContainer>
      <TablePagination
          component="div"
          count={items.totalElements || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>


      {/* Dialog Form */}
      <Dialog open={openDialog} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          Add Transportation {selectedItem?.salesOrderNo && `for ${selectedItem.salesOrderNo}`}
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            {transportationForms.map((form, index) => (
              <Paper
                key={index}
                sx={{ p: 2, mb: 2, position: "relative", backgroundColor: '#f5f5f5ff' }}
                elevation={3}
              >

                <IconButton
                  size="small"
                  onClick={() => handleRemoveForm(index)}
                  sx={{ position: "absolute", top: 5, right: 5, color: "red",fontSize:"16px" }}
                  disabled={transportationForms.length===1 ? true : false }
                >
                  <CloseIcon/>
                </IconButton>

              <Grid container alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Grid item>Unpaid</Grid>
                <Grid item>
                  <Tooltip
                    title={form.transaction ? "Once changed to paid, does not allow to unpaid" : ""}
                    arrow
                  >
                    <span>
                      <Switch
                        checked={form.transaction}
                        onChange={() => {
                          if (!form.transaction) {
                            handleFormChange(index, "transaction", true); // switch to true
                          }
                        }}
                        disabled={form.transaction} // lock if already paid
                      />
                    </span>
                  </Tooltip>
                </Grid>
                <Grid item>Paid</Grid>
              </Grid>

                <TextField
                  label="Mode of Transportation"
                  fullWidth
                  margin="normal"
                  required
                  value={form.modeOfTransportation}
                  onChange={(e) => handleFormChange(index, "modeOfTransportation", e.target.value)}
                />
                <TextField
                  label="Reference ID/ Invoice No./ LR No./ Any Other"
                  fullWidth
                  type="text"
                  margin="normal"
                  required
                  value={form.referenceID}
                  onChange={(e)=>handleFormChange(index, "referenceID", e.target.value)}
                />

                <TextField
                  label="Actual Transportation Cost (₹)"
                  type="number"
                  fullWidth
                  margin="normal"
                  required
                  value={form.actualTransportationCost}
                  onChange={(e) => handleFormChange(index, "actualTransportationCost", e.target.value)}
                />

                <TextField
                  label="Trip Date/ Transaction Date"
                  type="date"
                  fullWidth
                  margin="normal"
                  required
                  value={form.tripDate}
                  onChange={(e) => handleFormChange(index, "tripDate", e.target.value)}
                  InputLabelProps={{
                    shrink:true
                  }}
                />
                <TextField
                  label="SPOC Name"
                  type="text"
                  fullWidth
                  margin="normal"
                  required
                  value={form.spocName}
                  onChange={(e) => handleFormChange(index, "spocName", e.target.value)}
                />

                <Typography variant="subtitle" sx={{ mt: 0, mb: 0,fontWeight:"bold", color:"blue" }}>
                        Upload Transportation Bill:
                      </Typography>
                  <TextField
                        type="file"
                        support="image/*,"
                        fullWidth
                        margin="normal"
                        onChange={(e) => handleFormChange(index, "file", e.target.files[0])}
                      />

                      {/* Preview selected file */}
                      {form.file && (
                        <Box mt={1}>
                          <Typography variant="body2">Selected File: {form.file.name}</Typography>
                        </Box>
                      )}

              </Paper>
            ))}

            <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
              <Grid item>
                <Button variant="contained" onClick={handleAddAnotherTransportation}>
                  Add Another Transportation
                </Button>
              </Grid>
            </Grid>

            <DialogActions>
              <Button onClick={handleClose} color="secondary">Cancel</Button>
              <Button type="submit" variant="contained" color="primary">Submit</Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      {/**View dailog */}
        <Dialog open={openViewDialog} onClose={handleClose} fullWidth maxWidth="sm">
          <div>
          <DialogTitle>
            <Typography variant="h6">
              Transportation Details for {selectedItem?.salesOrderNo || "N/A"}
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            {selectedItem?.transportationDetails && selectedItem.transportationDetails.length > 0 ? (
              selectedItem.transportationDetails.map((d, index) => (
                <Paper
                  key={index}
                  elevation={2}
                  sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9' }}
                >
                  <Typography><strong>Mode of Transportation:</strong> {d.modeOfTransportation || "N/A"}</Typography>
                  <Typography><strong>Reference ID / Invoice No. / LR No. / Any Other:</strong> {d.referenceID || "N/A"}</Typography>
                  <Typography><strong>Actual Transportation Cost (₹):</strong> {d.actualTransportationCost || "N/A"}</Typography>
                  <Typography><strong>Trip Date/ Transaction Date:</strong> {d.tripDate?.split("T")[0] || "N/A"}</Typography>
                  <Typography><strong>SPOC Name:</strong> {d.spocName || "N/A"}</Typography>

                  <Typography variant="subtitle1" sx={{ mt:1, mb:1,fontWeight:"bold", color:"blue" }}>
                        Transportation Bill:
                      </Typography>

                    {d.fileUrl ? (
                      fileUrls[d.fileUrl] ? (
                        <img
                          src={fileUrls[d.fileUrl]}
                          alt="Transportation Bill"
                          crossOrigin="anonymous"
                          style={{ maxWidth: '100%', height: 'auto', marginBottom: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ color: "orange" }}>
                          Loading...
                        </Typography>
                      )
                    ) : (
                      <Typography variant="body2" sx={{ color: "orange" }}>
                        No bill uploaded
                      </Typography>
                    )}
                  <Typography>
                    <strong>Transaction:</strong>{" "}
                    {d.transaction === true ? (
                      <Typography component="span" sx={{ color: "green", fontWeight: "bold" }}>
                        Paid
                      </Typography>
                    ) : (
                      <Typography component="span" sx={{ color: "red", fontWeight: "bold" }}>
                        Unpaid
                      </Typography>
                    )}
                  </Typography>
                </Paper>
              ))
            ) : (
              <Typography>No transportation details available.</Typography>
            )}
          </DialogContent>

          {/* Total Transportation Cost outside map, above Close button */}
          {selectedItem?.transportationDetails && selectedItem.transportationDetails.length > 0 && (
            <Typography
              sx={{ p: 2, fontWeight: "bold", color: "green", textAlign: "right" }}
            >
              Total Transportation Cost: ₹
              {(selectedItem.transportationDetails.reduce(
                (sum, d) => sum + (Number(d.actualTransportationCost) || 0),
                0
              )).toFixed(2)} (incl. GST)
            </Typography>
          )}
          </div>

          <DownloadBillpdf
            ref={pdfRef}
            pdfContentRef={pdfContentRef}
            fileName={`Transportation_Details_${selectedItem?.salesOrderNo}.pdf`}/>

          <DialogActions>
             <Button variant="contained" color="success" onClick={() => pdfRef.current?.downloadPDF()}
                disabled={!selectedItem?.transportationDetails?.length}>
                Download Transportation Details
            </Button>
            <Button onClick={handleClose} color="primary">Close</Button>
          </DialogActions>
        </Dialog>
        <div style={{
              position: "absolute",
              top: "-9999px",
              left: "-9999px",
              opacity: 0,
              pointerEvents: "none"
            }}>
            <div ref={pdfContentRef} id="pdf-content" style={{ padding: 20, width: 600 }}>
              <h2>Transportation Details</h2>
              <p><strong>Sales Order:</strong> {selectedItem?.salesOrderNo || "N/A"}</p>
              <hr />
              {selectedItem?.transportationDetails?.length > 0 &&
                selectedItem.transportationDetails.map((d, index) => (
                  <div key={index} style={{ marginBottom: "20px" }}>
                    <p><strong>Mode of Transportation:</strong> {d.modeOfTransportation}</p>
                    <p><strong>Reference ID:</strong> {d.referenceID}</p>
                    <p><strong>Actual Cost:</strong> ₹{d.actualTransportationCost}</p>
                    <p><strong>Trip Date:</strong> {d.tripDate?.split("T")[0]}</p>
                    <p><strong>SPOC Name:</strong> {d.spocName}</p>

                    {d.fileUrl && fileUrls[d.fileUrl] && (
                      <img
                        src={fileUrls[d.fileUrl]}
                        alt="Bill"
                        style={{
                          width: "100%",
                          marginTop: "10px",
                          borderRadius: "5px",
                          border: "1px solid #ccc"
                        }}
                      />
                    )}

                    <p>
                      <strong>Transaction:</strong>
                      <span style={{ color: d.transaction ? "green" : "red" }}>
                        {d.transaction ? " Paid" : " Unpaid"}
                      </span>
                    </p>

                    <hr />
                  </div>
                ))}

              {selectedItem?.transportationDetails?.length > 0 && (
                <h3 style={{ textAlign: "right" }}>
                  Total Cost: ₹
                  {selectedItem.transportationDetails
                    .reduce((sum, d) => sum + Number(d.actualTransportationCost || 0), 0)
                    .toFixed(2)} (incl. GST)
                </h3>
              )}

            </div>
          </div>
    </>
  );
};

export default TransportationDashboard;